import React, { useState, useRef, useLayoutEffect, useMemo } from "react";
import {
    DisabledLayer,
    RenderRectangle,
    SelectionLayer,
} from ".";

const normalizeAngle = (angle = 0) =>
    ((Number(angle) % 360) + 360) % 360;

const getDynamicDimensions = (angle, dimensions) => {
    const normalized = normalizeAngle(angle);

    if (normalized === 90 || normalized === 270) {
        return {
            width: dimensions.height,
            height: dimensions.width,
        };
    }

    return {
        width: dimensions.width,
        height: dimensions.height,
    };
};

const RenderImage = (props) => {
    const imageList = props.images || [];
    const highlightCoordinates = props.highlightCoordinates || [];
    const disabledPages = props.disabledPages || [];
    const rotatedPages = props.rotatedPages || {};

    const zoom = Number(props.zoomLevel) || 1;
    const containerWidth = props.pdfContainerDimensions?.width ?? 0;

    const containerRef = useRef(null);
    const [contentHeight, setContentHeight] = useState(0); // unscaled height

    // Group highlights by page once instead of filtering for every page
    const highlightsByPage = useMemo(() => {
        const map = new Map();
        for (const item of highlightCoordinates) {
            const key = String(item.page);
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(item);
        }
        return map;
    }, [highlightCoordinates]);

    const disabledSet = useMemo(
        () => new Set(disabledPages.map(String)),
        [disabledPages]
    );

    // Widest page (unscaled), calculated from data so it is never stale
    const maxUnscaledWidth = useMemo(() => {
        let max = 0;
        for (const obj of imageList) {
            const w = Number(obj.width);
            const h = Number(obj.height);
            if (!(w > 0) || !(h > 0)) continue;

            const rotation = normalizeAngle(rotatedPages[obj.page] ?? 0);
            const { width } = getDynamicDimensions(rotation, {
                width: w,
                height: h,
            });
            max = Math.max(max, width);
        }
        return max;
    }, [imageList, rotatedPages]);

    // Unscaled content height (offsetHeight ignores CSS transforms)
    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const measure = () => {
            const next = el.offsetHeight;
            setContentHeight((prev) => (prev === next ? prev : next));
        };

        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [imageList, rotatedPages]);

    const scaledWidth = maxUnscaledWidth * zoom;
    const scaledHeight = contentHeight * zoom;
    const shouldCenter = scaledWidth < containerWidth;

    return (
        // Outer box takes the SCALED size in layout, so scroll area and centering are correct
        <div
            className="pdf-viewer-scale-wrapper"
            style={{
                width: scaledWidth,
                height: scaledHeight,
                margin: shouldCenter ? "0 auto" : 0,
            }}
        >
            {/* Inner box is full size and visually scaled from the top-left */}
            <div
                ref={containerRef}
                className="pdf-viewer-image-container"
                style={{
                    width: maxUnscaledWidth,
                    transform: `scale(${zoom})`,
                    transformOrigin: "left top",
                }}
            >
                {imageList.map((obj, index) => {
                    const rotation = normalizeAngle(
                        rotatedPages[obj.page] ?? 0
                    );

                    const original = {
                        width: Number(obj.width),
                        height: Number(obj.height),
                    };

                    if (
                        !Number.isFinite(original.width) ||
                        !Number.isFinite(original.height) ||
                        original.width <= 0 ||
                        original.height <= 0
                    ) {
                        return null;
                    }

                    const page = getDynamicDimensions(rotation, original);

                    const pageHighlights =
                        highlightsByPage.get(String(obj.page)) ?? [];

                    const isDisabled = disabledSet.has(String(obj.page));

                    return (
                        <div
                            key={obj.page ?? index}
                            className="pdf-viewer-item"
                            style={{
                                width: page.width,
                                height: page.height,
                            }}
                        >
                            <div
                                className="pdf-viewer-rotated-content"
                                style={{
                                    width: original.width,
                                    height: original.height,
                                    transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
                                }}
                            >
                                <img
                                    className="pdf-viewer-item-image"
                                    src={obj.url}
                                    alt={`PDF page ${obj.page}`}
                                    draggable={false}
                                />

                                {pageHighlights.length > 0 && (
                                    <svg
                                        className="pdf-viewer-highlight-container"
                                        width={original.width}
                                        height={original.height}
                                        viewBox={`0 0 ${original.width} ${original.height}`}
                                        preserveAspectRatio="none"
                                    >
                                        <RenderRectangle
                                            coordinates={pageHighlights}
                                            imageDimensions={original}
                                            highlight
                                            id={`pdf-highlight-id-${obj.page}`}
                                        />
                                    </svg>
                                )}

                                <DisabledLayer
                                    {...obj}
                                    disabled={isDisabled}
                                    imageDimensions={original}
                                />

                                <SelectionLayer
                                    {...obj}
                                    rotateAngle={rotation}
                                    disabled={isDisabled}
                                    selectionEnabled={props.selectionEnabled}
                                    imageDimensions={original}
                                    selectedPages={props.selectedPages}
                                    handleSelection={props.handleSelection}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default RenderImage;